import { createFileRoute } from "@tanstack/react-router";
import {
  Typography,
  Table,
  Button,
  Tag,
  Flex,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  App,
  Alert,
  Spin,
  theme,
} from "antd";
import { PlusOutlined, DeleteOutlined, CopyOutlined, KeyOutlined } from "@ant-design/icons";
import { useState } from "react";
import { useQ_PageSettings_ApiKeys } from "@/hooks/OrgSettings/useQ_PageSettings_ApiKeys";
import {
  useM_PageSettings_ApiKeyGenerate,
  type ApiKeyGenerateResult,
} from "@/hooks/OrgSettings/useM_PageSettings_ApiKeyGenerate";
import { useM_PageSettings_ApiKeyRevoke } from "@/hooks/OrgSettings/useM_PageSettings_ApiKeyRevoke";
import type { PageSettings_ApiKeys_QueryData } from "@/hooks/OrgSettings/useQ_PageSettings_ApiKeys";
import type { Supabase_Enums } from "@gotoIHUB/db-types";

export const Route = createFileRoute(
  "/_protected/$organizationId/$flowId/settings/api-keys",
)({
  component: Page_Settings_ApiKeys,
});

const SCOPE_COLORS: Record<string, string> = {
  tenant: "blue",
  admin: "purple",
  reports: "green",
};

function Page_Settings_ApiKeys() {
  const { token } = theme.useToken();
  const { organizationId } = Route.useParams();
  const { message, modal } = App.useApp();
  const qApiKeys = useQ_PageSettings_ApiKeys({ organizationId });
  const mGenerate = useM_PageSettings_ApiKeyGenerate({ organizationId });
  const mRevoke = useM_PageSettings_ApiKeyRevoke({ organizationId });
  const [generateModalOpen, setGenerateModalOpen] = useState(false);
  const [generatedKey, setGeneratedKey] = useState<ApiKeyGenerateResult | null>(null);
  const [generateForm] = Form.useForm();

  const handleGenerate = async (values: {
    label: string;
    // Narrower than the column on purpose: `webhook` survives in the enum for
    // the sake of not rewriting the type, but the hub no longer verifies
    // callbacks and the generate function rejects it.
    scope: Exclude<Supabase_Enums<"api_key_scope">, "webhook">;
    allowed_origins?: string[];
    is_test?: boolean;
  }) => {
    try {
      const result = await mGenerate.mutation.mutateAsync({
        label: values.label,
        scope: values.scope,
        allowedOrigins: values.allowed_origins,
        isTest: values.is_test,
      });
      setGenerateModalOpen(false);
      generateForm.resetFields();
      setGeneratedKey(result);
    } catch {
      message.error("Failed to generate API key");
    }
  };

  const handleRevoke = (keyId: string, label: string) => {
    modal.confirm({
      title: "Revoke API Key",
      content: `Are you sure you want to revoke "${label}"? This cannot be undone. Any applications using this key will lose access.`,
      okText: "Revoke",
      okType: "danger",
      onOk: async () => {
        try {
          await mRevoke.mutation.mutateAsync(keyId);
          message.success("API key revoked");
        } catch {
          message.error("Failed to revoke API key");
        }
      },
    });
  };

  const columns = [
    {
      title: "Prefix",
      dataIndex: "key_prefix",
      key: "key_prefix",
      render: (prefix: string) => (
        <Typography.Text code style={{ fontFamily: "monospace" }}>
          {prefix}...
        </Typography.Text>
      ),
    },
    {
      title: "Label",
      dataIndex: "label",
      key: "label",
      render: (label: string, record: PageSettings_ApiKeys_QueryData[number]) => (
        <Flex align="center" gap={8}>
          <span>{label}</span>
          {record.is_test ? <Tag color="gold">TEST</Tag> : null}
        </Flex>
      ),
    },
    {
      title: "Scope",
      dataIndex: "scope",
      key: "scope",
      render: (scope: string) => <Tag color={SCOPE_COLORS[scope] || "default"}>{scope}</Tag>,
    },
    {
      title: "Origins",
      dataIndex: "allowed_origins",
      key: "allowed_origins",
      render: (origins: string[]) =>
        origins?.length > 0 ? origins.map((o) => <Tag key={o}>{o}</Tag>) : (
          <Typography.Text type="secondary">Any</Typography.Text>
        ),
    },
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created_at",
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: "Last Used",
      dataIndex: "last_used_at",
      key: "last_used_at",
      render: (date: string | null) =>
        date ? new Date(date).toLocaleDateString() : (
          <Typography.Text type="secondary">Never</Typography.Text>
        ),
    },
    {
      title: "",
      key: "actions",
      render: (_: unknown, record: PageSettings_ApiKeys_QueryData[number]) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          size="small"
          onClick={() => handleRevoke(record.id, record.label)}
          loading={mRevoke.mutation.isPending}
        >
          Revoke
        </Button>
      ),
    },
  ];

  if (qApiKeys.query.isLoading) {
    return (
      <Flex justify="center" style={{ padding: token.paddingXL }}>
        <Spin />
      </Flex>
    );
  }

  return (
    <div>
      <Flex justify="space-between" align="center" style={{ marginBottom: token.marginLG }}>
        <Typography.Text type="secondary">
          {qApiKeys.apiKeys.length} active key{qApiKeys.apiKeys.length !== 1 ? "s" : ""}
        </Typography.Text>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setGenerateModalOpen(true)}>
          Generate API Key
        </Button>
      </Flex>

      <Table<PageSettings_ApiKeys_QueryData[number]>
        dataSource={qApiKeys.apiKeys}
        columns={columns}
        rowKey="id"
        pagination={false}
        scroll={{ x: "max-content" }}
        locale={{ emptyText: "No API keys yet. Generate one to get started." }}
      />

      <Modal
        title="Generate API Key"
        open={generateModalOpen}
        onCancel={() => {
          setGenerateModalOpen(false);
          generateForm.resetFields();
        }}
        onOk={() => generateForm.submit()}
        confirmLoading={mGenerate.mutation.isPending}
        destroyOnHidden
      >
        <Form form={generateForm} layout="vertical" onFinish={handleGenerate}>
          <Form.Item
            name="label"
            label="Label"
            rules={[{ required: true, message: "Label is required" }]}
          >
            <Input placeholder="e.g. Production, Staging, SR22team" />
          </Form.Item>
          <Form.Item
            name="scope"
            label="Scope"
            rules={[{ required: true, message: "Scope is required" }]}
            initialValue="tenant"
          >
            <Select
              options={[
                { value: "tenant", label: "Tenant — session API access" },
                { value: "admin", label: "Admin — full org management" },
                { value: "reports", label: "Reports — read lead health only" },
              ]}
            />
          </Form.Item>
          <Form.Item name="allowed_origins" label="Allowed Origins (optional)">
            <Select
              mode="tags"
              placeholder="e.g. https://sr22team.com (leave empty for any)"
              tokenSeparators={[","]}
            />
          </Form.Item>
          <Form.Item
            name="is_test"
            label="Test key (dry-run)"
            valuePropName="checked"
            initialValue={false}
            tooltip="Sessions created with this key run in dry-run mode — no real carrier purchase. Use for staging/testing."
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={
          <Flex align="center" gap={token.marginSM}>
            <KeyOutlined />
            <span>API Key Generated</span>
          </Flex>
        }
        open={!!generatedKey}
        onCancel={() => setGeneratedKey(null)}
        onOk={() => setGeneratedKey(null)}
        okText="Done"
        cancelButtonProps={{ style: { display: "none" } }}
        destroyOnHidden
      >
        <Alert
          type="warning"
          message="Copy this key now — it will not be shown again"
          style={{ marginBottom: token.marginMD }}
        />
        <Typography.Paragraph>
          <Typography.Text strong>Your API Key:</Typography.Text>
        </Typography.Paragraph>
        <Typography.Paragraph
          copyable={{
            icon: [<CopyOutlined key="copy" />, <CopyOutlined key="copied" />],
            tooltips: ["Copy key", "Copied!"],
          }}
          code
          style={{
            padding: token.paddingSM,
            background: token.colorBgLayout,
            borderRadius: token.borderRadius,
            wordBreak: "break-all",
          }}
        >
          {generatedKey?.plaintextKey}
        </Typography.Paragraph>
        <Typography.Paragraph type="secondary" style={{ fontSize: token.fontSizeSM }}>
          Prefix: <Typography.Text code>{generatedKey?.keyPrefix}</Typography.Text> — use this
          to identify the key in the list above.
        </Typography.Paragraph>
      </Modal>
    </div>
  );
}
